import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { PenLine, Crown, ArrowUp, ArrowDown, Filter, X, Search } from 'lucide-react';
import { sortPlayersByElo } from '../utils/eloUtils';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import EditProfile from './EditProfile';
import { cn } from '../utils/cn';

const Dashboard = ({ session, supabase }) => {
  const [profile, setProfile] = useState(null);
  const [players, setPlayers] = useState([]);
  const [games, setGames] = useState([]);
  const [filteredGames, setFilteredGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingProfile, setEditingProfile] = useState(false);
  const [activeTab, setActiveTab] = useState('rankings');
  const [isPwa, setIsPwa] = useState(false);
  const [playerFilter, setPlayerFilter] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [playerSearch, setPlayerSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showPlayerSearch, setShowPlayerSearch] = useState(false);
  const searchInputRef = useRef(null);
  const searchResultsRef = useRef(null);

  useEffect(() => {
    // Check if running as PWA
    if (window.matchMedia('(display-mode: standalone)').matches || 
        window.navigator.standalone === true) {
      setIsPwa(true);
    }
    
    // Fetch user profile data, ELO rankings, and game history
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Check if Supabase is properly initialized
        if (!supabase || !supabase.from) {
          console.error('Supabase client is not properly initialized. Check your environment variables.');
          throw new Error('Database connection error. Please check your configuration.');
        }

        // Fetch profile from unified players table
        const { data: profileData, error: profileError } = await supabase
          .from('players')
          .select('*')
          .eq('user_id', session.user.id)
          .single();

        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Error fetching profile:', profileError);
        } else if (profileData) {
          setProfile(profileData);
        }

        // Fetch all players for ELO rankings
        const { data: allPlayersData, error: allPlayersError } = await supabase
          .from('players')
          .select('id, username, elo, avatar_url')
          .eq('is_active', true)
          .order('elo', { ascending: false });

        if (allPlayersError) {
          throw allPlayersError;
        }

        // Set ELO rankings
        const playersWithElo = allPlayersData.map(player => ({
          ...player,
          elo: player.elo || 1000
        }));

        setPlayers(playersWithElo);
        
        // Fetch ALL game history (not just user's games)
        const { data: gameHistoryData, error: gameHistoryError } = await supabase
          .from('games')
          .select(`
            id, 
            created_at,
            team1_score,
            team2_score,
            team1_player1,
            team1_player2,
            team2_player1,
            team2_player2,
            team1_player1_elo_change,
            team1_player2_elo_change,
            team2_player1_elo_change,
            team2_player2_elo_change,
            team1_player1_elo_before,
            team1_player1_elo_after,
            team1_player2_elo_before,
            team1_player2_elo_after,
            team2_player1_elo_before,
            team2_player1_elo_after,
            team2_player2_elo_before,
            team2_player2_elo_after
          `)
          .order('created_at', { ascending: false })
          .limit(50);
          
        if (gameHistoryError) {
          throw gameHistoryError;
        }
        
        // Fetch player data separately
        let processedGames = [];
        if (gameHistoryData && gameHistoryData.length > 0) {
          // Get unique player IDs from all team players
          const playerIds = [...new Set([
            ...gameHistoryData.map(game => game.team1_player1),
            ...gameHistoryData.map(game => game.team1_player2),
            ...gameHistoryData.map(game => game.team2_player1),
            ...gameHistoryData.map(game => game.team2_player2)
          ])].filter(id => id !== null);
          
          // Fetch all players in one query
          const { data: playersData } = await supabase
            .from('players')
            .select('id, username, avatar_url')
            .in('id', playerIds);
          
          // Create a map of player data by ID for easy lookup
          const playerMap = {};
          if (playersData) {
            playersData.forEach(player => {
              playerMap[player.id] = player;
            });
          }
          
          // Process games to transform them to winner/loser format
          processedGames = gameHistoryData.map(game => {
            // Determine winner and loser teams
            const team1Won = game.team1_score > game.team2_score;
            
            // Get player objects, handling nulls
            const team1Player1 = game.team1_player1 ? playerMap[game.team1_player1] : null;
            const team1Player2 = game.team1_player2 ? playerMap[game.team1_player2] : null;
            const team2Player1 = game.team2_player1 ? playerMap[game.team2_player1] : null;
            const team2Player2 = game.team2_player2 ? playerMap[game.team2_player2] : null;
            
            // Create placeholder for unknown players
            const unknownPlayer = { username: 'Unknown Player', avatar_url: null };
            
            // Create a representation that matches your UI expectations
            return {
              id: game.id,
              created_at: game.created_at,
              // The "winner" is now the first player of the winning team
              winner_id: team1Won ? game.team1_player1 : game.team2_player1,
              loser_id: team1Won ? game.team2_player1 : game.team1_player1,
              winner: team1Won ? (team1Player1 || { id: game.team1_player1, ...unknownPlayer }) 
                              : (team2Player1 || { id: game.team2_player1, ...unknownPlayer }),
              loser: team1Won ? (team2Player1 || { id: game.team2_player1, ...unknownPlayer }) 
                              : (team1Player1 || { id: game.team1_player1, ...unknownPlayer }),
              // Get ELO changes directly from the games table
              team1_player1_elo_change: game.team1_player1_elo_change || 0,
              team1_player2_elo_change: game.team1_player2_elo_change || 0,
              team2_player1_elo_change: game.team2_player1_elo_change || 0,
              team2_player2_elo_change: game.team2_player2_elo_change || 0,
              // Add ELO before/after data
              team1_player1_elo_before: game.team1_player1_elo_before || 0,
              team1_player1_elo_after: game.team1_player1_elo_after || 0,
              team1_player2_elo_before: game.team1_player2_elo_before || 0,
              team1_player2_elo_after: game.team1_player2_elo_after || 0,
              team2_player1_elo_before: game.team2_player1_elo_before || 0,
              team2_player1_elo_after: game.team2_player1_elo_after || 0,
              team2_player2_elo_before: game.team2_player2_elo_before || 0,
              team2_player2_elo_after: game.team2_player2_elo_after || 0,
              // Add team info for reference
              team1_score: game.team1_score,
              team2_score: game.team2_score,
              team1_player1: team1Player1 || (game.team1_player1 ? { id: game.team1_player1, ...unknownPlayer } : null),
              team1_player2: team1Player2 || (game.team1_player2 ? { id: game.team1_player2, ...unknownPlayer } : null),
              team2_player1: team2Player1 || (game.team2_player1 ? { id: game.team2_player1, ...unknownPlayer } : null),
              team2_player2: team2Player2 || (game.team2_player2 ? { id: game.team2_player2, ...unknownPlayer } : null),
              // Add a flag to indicate which team won
              team1Won: team1Won
            };
          });
        }

        setGames(processedGames || []);
        setFilteredGames(processedGames || []);

      } catch (error) {
        console.error('Error fetching dashboard data:', error.message);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (session) {
      fetchInitialData();
    }
  }, [session, supabase]);

  // Apply player filter to games
  useEffect(() => {
    if (playerFilter) {
      setFilteredGames(games.filter(game => 
        game.winner.id === playerFilter.id || 
        game.loser.id === playerFilter.id ||
        (game.team1_player2 && game.team1_player2.id === playerFilter.id) ||
        (game.team2_player2 && game.team2_player2.id === playerFilter.id)
      ));
    } else {
      setFilteredGames(games);
    }
  }, [playerFilter, games]);

  // Handle player search
  const handlePlayerSearchChange = (e) => {
    const value = e.target.value;
    setPlayerSearch(value);
    
    if (value.trim() === '') {
      setSearchResults([]);
      return;
    }
    
    // Filter players based on search term
    const filteredPlayers = players.filter(player => 
      player.username.toLowerCase().includes(value.toLowerCase())
    );
    
    setSearchResults(filteredPlayers.slice(0, 10)); // Limit to 10 results
  };
  
  // Handle click outside of search results
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchInputRef.current && 
        !searchInputRef.current.contains(event.target) &&
        searchResultsRef.current && 
        !searchResultsRef.current.contains(event.target)
      ) {
        setShowPlayerSearch(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (loading) {
    return (
      <div className="container px-4 mx-auto max-w-4xl py-6">
        <Tabs defaultValue="rankings" value={activeTab}>
          <TabsList className="grid w-full grid-cols-3 mb-8">
            <TabsTrigger value="rankings">Rankings</TabsTrigger>
            <TabsTrigger value="history">Game History</TabsTrigger>
            <TabsTrigger value="profile">My Profile</TabsTrigger>
          </TabsList>
          
          <TabsContent value={activeTab} className="min-h-[60vh]">
            <div className="flex flex-col items-center justify-center h-full py-12">
              <div className="space-y-6">
                <div className="flex space-x-2 animate-pulse">
                  <div className="w-3 h-3 bg-primary rounded-full"></div>
                  <div className="w-3 h-3 bg-primary rounded-full" style={{ animationDelay: "0.2s" }}></div>
                  <div className="w-3 h-3 bg-primary rounded-full" style={{ animationDelay: "0.4s" }}></div>
                </div>
                <div className="flex flex-col items-center space-y-3">
                  <div className="h-8 w-32 bg-muted rounded animate-pulse"></div>
                  <div className="h-64 w-full max-w-md bg-card rounded-lg animate-pulse opacity-70"></div>
                  <div className="h-64 w-full max-w-md bg-card rounded-lg animate-pulse opacity-50"></div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative max-w-md mx-auto">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
          <p className="mt-2 text-sm">
            Check your browser console for more details and ensure your Supabase connection is properly configured.
          </p>
          <Button 
            onClick={() => window.location.reload()} 
            className="mt-4 bg-red-600 hover:bg-red-700 text-white"
          >
            Reload Page
          </Button>
        </div>
      </div>
    );
  }

  // Handle profile update success
  const handleProfileUpdated = (updatedProfile) => {
    setProfile(updatedProfile);
    setEditingProfile(false);

    // Update ELO rankings to reflect username change
    setPlayers(prevPlayers =>
      sortPlayersByElo(
        prevPlayers.map(player =>
          player.id === updatedProfile.id
            ? { 
                ...player, 
                username: updatedProfile.username, 
                elo: updatedProfile.elo || 1000,
                avatar_url: updatedProfile.avatar_url 
              }
            : player
        )
      )
    );
  };

  const getInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : 'U';
  };

  const formatDate = (dateString, format = 'default') => {
    const date = new Date(dateString);
    
    if (format === 'date-only') {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric',
        month: 'short', 
        day: 'numeric'
      });
    }
    
    if (format === 'time-only') {
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    // Default full format
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Toggle filter by player
  const handlePlayerFilterClick = (player) => {
    if (playerFilter?.id === player.id) {
      setPlayerFilter(null);
    } else {
      setPlayerFilter(player);
    }
    setShowFilters(false);
  };

  // Clear all filters
  const clearFilters = () => {
    setPlayerFilter(null);
  };

  // Render the top players with a new design
  const renderTopPlayers = () => {
    // Only display if we have players
    if (players.length === 0) return null;
    
    // Get top 4 players (or fewer if we don't have 4)
    const topPlayers = players.slice(0, Math.min(4, players.length));
    
    return (
      <div className="mb-10">
        <h2 className="text-2xl font-bold mb-6">Leaderboard</h2>
        <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
          {/* Champion Card - First Place */}
          {topPlayers.length >= 1 && (
            <div className="md:col-span-3">
              <Card className="overflow-hidden border-0 shadow-lg bg-gradient-to-br from-yellow-500/20 via-amber-500/10 to-yellow-500/5">
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row items-center">
                    {/* Left Side - Champion Badge */}
                    <div className="p-6 flex flex-col items-center justify-center md:w-1/3 bg-gradient-to-br from-yellow-500/30 to-amber-600/20">
                      <div className="relative mb-2">
                        <div className="absolute -top-3 -left-3 bg-yellow-500 text-yellow-950 w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold z-10 shadow-md">1</div>
                        <div className="absolute -top-1 -left-1 w-12 h-12 rounded-full bg-gradient-to-br from-yellow-300 to-amber-500 blur-md opacity-30"></div>
                        <Avatar className="h-28 w-28 ring-4 ring-yellow-500 ring-offset-2 ring-offset-background shadow-xl">
                          {topPlayers[0].avatar_url ? (
                            <AvatarImage src={topPlayers[0].avatar_url} alt={topPlayers[0].username} />
                          ) : (
                            <AvatarFallback className="bg-gradient-to-br from-yellow-400 to-amber-600 text-white text-3xl">
                              {getInitial(topPlayers[0].username)}
                            </AvatarFallback>
                          )}
                        </Avatar>
                      </div>
                      <Crown className="h-10 w-10 text-yellow-500 drop-shadow-md mb-2" />
                      <span className="text-sm font-semibold text-amber-600 uppercase tracking-wider">Champion</span>
                    </div>
                    
                    {/* Right Side - Player Info */}
                    <div className="p-6 flex-1 flex flex-col items-center md:items-start">
                      <div className="flex items-center mb-3">
                        <h3 className="text-2xl font-bold mr-2">{topPlayers[0].username}</h3>
                        {profile?.id === topPlayers[0].id && (
                          <span className="bg-primary/20 text-primary text-xs px-2 py-1 rounded-full">You</span>
                        )}
                      </div>
                      
                      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-full px-4 py-2 mb-4">
                        <span className="text-xl font-bold text-yellow-600">{Math.round(topPlayers[0].elo)} ELO</span>
                      </div>
                      
                      <div className="text-sm text-muted-foreground">
                        Leading by {topPlayers.length > 1 ? Math.round(topPlayers[0].elo - topPlayers[1].elo) : 0} points
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          
          {/* Challenger Cards - 2nd to 5th Place */}
          {topPlayers.slice(1).map((player, index) => {
            const rank = index + 2; // Rank 2, 3, 4, 5
            let accentColor, bgGradient, borderColor;
            
            // Different styling based on rank
            switch(rank) {
              case 2:
                accentColor = "text-gray-400";
                bgGradient = "from-gray-400/20 to-gray-500/5";
                borderColor = "border-gray-400/30";
                break;
              case 3:
                accentColor = "text-amber-700";
                bgGradient = "from-amber-700/20 to-amber-800/5";
                borderColor = "border-amber-700/30";
                break;
              default:
                accentColor = "text-primary";
                bgGradient = "from-primary/10 to-primary/5";
                borderColor = "border-primary/20";
            }
            
            return (
              <Card key={player.id} className={cn(
                "overflow-hidden border",
                rank === 2 && "border-gray-400/30 bg-gradient-to-br from-gray-400/20 to-gray-500/5",
                rank === 3 && "border-amber-700/30 bg-gradient-to-br from-amber-700/20 to-amber-800/5",
                rank > 3 && "border-primary/20 bg-gradient-to-br from-primary/10 to-primary/5"
              )}>
                <CardContent className="p-4">
                  <div className="flex items-center">
                    <div className="relative mr-4">
                      <div className={cn(
                        "absolute -top-2 -left-2 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold z-10",
                        rank === 2 && "bg-gray-400 text-white",
                        rank === 3 && "bg-amber-700 text-white",
                        rank > 3 && "bg-primary text-primary-foreground"
                      )}>
                        {rank}
                      </div>
                      <Avatar className={cn(
                        "h-16 w-16 ring-2 ring-offset-1 ring-offset-background",
                        rank === 2 && "ring-gray-400",
                        rank === 3 && "ring-amber-700",
                        rank > 3 && "ring-primary"
                      )}>
                        {player.avatar_url ? (
                          <AvatarImage src={player.avatar_url} alt={player.username} />
                        ) : (
                          <AvatarFallback className={cn(
                            "text-xl",
                            rank === 2 && "bg-gray-400/20 text-gray-400",
                            rank === 3 && "bg-amber-700/20 text-amber-700",
                            rank > 3 && "bg-primary/20 text-primary"
                          )}>
                            {getInitial(player.username)}
                          </AvatarFallback>
                        )}
                      </Avatar>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center mb-1">
                        <h3 className="text-base font-semibold mr-2">{player.username}</h3>
                        {profile?.id === player.id && (
                          <span className="bg-primary/20 text-primary text-xs px-1.5 py-0.5 rounded-full text-[10px]">You</span>
                        )}
                      </div>
                      
                      <div className={cn(
                        "inline-block text-sm rounded-full px-2 py-0.5 font-semibold",
                        rank === 2 && "bg-gray-400/10 text-gray-400",
                        rank === 3 && "bg-amber-700/10 text-amber-700",
                        rank > 3 && "bg-primary/10 text-primary"
                      )}>
                        {Math.round(player.elo)} ELO
                      </div>
                      
                      {rank === 2 && topPlayers.length > 2 && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          +{Math.round(player.elo - topPlayers[2].elo)} above 3rd place
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  };

  // Render a game card
  const renderGameCard = (game, index) => {
    // Use the flag from the processed game object
    const team1Won = game.team1Won;
    
    // Check if the current user is on either team
    const isWinner = profile && (
      profile.id === game.winner_id || 
      (game.team1_player2 && profile.id === game.team1_player2.id && team1Won) ||
      (game.team2_player2 && profile.id === game.team2_player2.id && !team1Won)
    );
    
    const isLoser = profile && (
      profile.id === game.loser_id || 
      (game.team1_player2 && profile.id === game.team1_player2.id && !team1Won) ||
      (game.team2_player2 && profile.id === game.team2_player2.id && team1Won)
    );
    
    const isInvolved = isWinner || isLoser;
    
    // Check if this game involves the filtered player
    const isHighlighted = playerFilter && (
      playerFilter.id === game.winner.id || 
      playerFilter.id === game.loser.id ||
      (game.team1_player2 && playerFilter.id === game.team1_player2.id) ||
      (game.team2_player2 && playerFilter.id === game.team2_player2.id)
    );

    // Render an ELO change badge
    const renderEloChange = (eloChange, eloBefore, eloAfter) => (
      <div className={cn(
        "text-sm mt-1 font-medium flex items-center",
        eloChange > 0 ? 'text-green-500' : 'text-red-500',
        isPwa && "text-xs mt-0.5 flex-nowrap" // Slightly larger text on mobile
      )}>
        {eloChange > 0 ? <ArrowUp className={cn("h-3 w-3 mr-1", isPwa && "h-2 w-2")} /> : 
                         <ArrowDown className={cn("h-3 w-3 mr-1", isPwa && "h-2 w-2")} />}
        {eloChange > 0 ? '+' : ''}{eloChange}
        {!isPwa && " ELO"}
        {!isPwa && (
          <span className="ml-1 text-xs text-muted-foreground">
            ({eloBefore} → {eloAfter})
          </span>
        )}
      </div>
    );

    // Special handling for player cards
    const renderPlayerCard = (player, eloChange, eloBefore, eloAfter, isTeamWinner) => {
      if (!player) return null;
      
      const isCurrentUser = profile && profile.id === player.id;
      
      return (
        <div className={cn(
          "flex items-center space-x-2",
          isPwa && "mb-1" // Reduce margin for mobile
        )}>
          <Avatar className={cn(
            "h-8 w-8 ring-1 ring-offset-1 ring-offset-background",
            isTeamWinner ? "ring-green-500" : "ring-red-500", // Red ring for losers
            isPwa && "h-7 w-7" // Slightly smaller avatar on mobile
          )}>
            {player.avatar_url ? (
              <AvatarImage src={player.avatar_url} alt={player.username} />
            ) : (
              <AvatarFallback className={isTeamWinner 
                ? "bg-primary/10 text-primary-foreground" 
                : "bg-red-500/10 text-red-500"
              }>
                {getInitial(player.username)}
              </AvatarFallback>
            )}
          </Avatar>
          <div className={cn(isPwa && "max-w-[80px]")}>
            <div className={cn(
              "text-sm font-medium",
              isPwa && "text-xs"
            )}>
              {player.username}
              {isCurrentUser && <span className="ml-1 text-xs text-primary">{isPwa ? "•" : "(You)"}</span>}
            </div>
            {eloChange !== 0 && renderEloChange(eloChange, eloBefore, eloAfter)}
          </div>
        </div>
      );
    };
    
    return (
      <Card 
        key={game.id} 
        className={cn(
          "overflow-hidden transition-all duration-200 hover:shadow-md animate-fadeIn",
          isInvolved && "border-l-4",
          isWinner && "border-l-green-500",
          isLoser && "border-l-red-500",
          isHighlighted && !isInvolved && "border-l-4 border-l-primary"
        )}
        style={{ animationDelay: `${index * 100}ms` }}
      >
        <CardContent className={cn(
          "p-4 bg-gradient-to-r from-transparent to-muted/30",
          isPwa && "p-3" // Adjust padding for mobile/PWA
        )}>
          <div className="flex flex-col">
            {/* Score and teams display */}
            <div className={cn(
              "flex items-stretch justify-between",
              isPwa && "gap-2 items-center" // Keep horizontal on mobile but adjust spacing
            )}>
              {/* Team 1 */}
              <div className={cn(
                "flex flex-col p-3 rounded-lg",
                team1Won 
                  ? "bg-green-500/10 border border-green-500/30" 
                  : "bg-red-500/10 border border-red-500/30",
                team1Won && "shadow-[0_0_10px_rgba(34,197,94,0.2)]", // Add glow effect for winner
                !team1Won && "shadow-[0_0_10px_rgba(239,68,68,0.2)]", // Add red glow for loser
                isPwa && "p-2 flex-1" // Reduce padding and use flex-1 on mobile
              )}>
                <div className="flex items-center justify-between mb-2">
                  <div className={cn(
                    "font-bold text-2xl",
                    team1Won ? "text-green-400" : "text-red-400",
                    isPwa && "text-xl" // Slightly smaller text on mobile
                  )}>{game.team1_score}</div>
                  {team1Won 
                    ? <div className="text-xs font-semibold uppercase text-green-500">Winner</div>
                    : <div className="text-xs font-semibold uppercase text-red-500">Loser</div>
                  }
                </div>
                
                <div className="space-y-2">
                  {renderPlayerCard(
                    game.team1_player1,
                    game.team1_player1_elo_change,
                    game.team1_player1_elo_before,
                    game.team1_player1_elo_after,
                    team1Won
                  )}
                  
                  {game.team1_player2 && renderPlayerCard(
                    game.team1_player2,
                    game.team1_player2_elo_change,
                    game.team1_player2_elo_before,
                    game.team1_player2_elo_after,
                    team1Won
                  )}
                </div>
              </div>
              
              {/* VS divider */}
              <div className={cn(
                "text-xl font-bold text-primary/70 flex items-center",
                isPwa ? "justify-center mx-1 flex-shrink-0" : "mx-4 flex-col justify-center" // Center horizontally with less spacing on PWA
              )}>
                <span>vs</span>
                {!isPwa && (team1Won ? (
                  <div className="flex mt-1 items-center">
                    <div className="h-2 w-2 rounded-full bg-green-500 mr-1"></div>
                    <div className="h-2 w-8 rounded-full bg-green-500/30"></div>
                  </div>
                ) : (
                  <div className="flex mt-1 items-center">
                    <div className="h-2 w-8 rounded-full bg-red-500/30"></div>
                    <div className="h-2 w-2 rounded-full bg-red-500 ml-1"></div>
                  </div>
                ))}
              </div>
              
              {/* Team 2 */}
              <div className={cn(
                "flex flex-col p-3 rounded-lg",
                !team1Won 
                  ? "bg-green-500/10 border border-green-500/30" 
                  : "bg-red-500/10 border border-red-500/30",
                !team1Won && "shadow-[0_0_10px_rgba(34,197,94,0.2)]", // Add glow effect for winner
                team1Won && "shadow-[0_0_10px_rgba(239,68,68,0.2)]", // Add red glow for loser
                isPwa && "p-2 flex-1" // Reduce padding and use flex-1 on mobile
              )}>
                <div className="flex items-center justify-between mb-2">
                  <div className={cn(
                    "font-bold text-2xl",
                    !team1Won ? "text-green-400" : "text-red-400",
                    isPwa && "text-xl" // Slightly smaller text on mobile
                  )}>{game.team2_score}</div>
                  {!team1Won 
                    ? <div className="text-xs font-semibold uppercase text-green-500">Winner</div>
                    : <div className="text-xs font-semibold uppercase text-red-500">Loser</div>
                  }
                </div>
                
                <div className="space-y-2">
                  {renderPlayerCard(
                    game.team2_player1,
                    game.team2_player1_elo_change,
                    game.team2_player1_elo_before,
                    game.team2_player1_elo_after,
                    !team1Won
                  )}
                  
                  {game.team2_player2 && renderPlayerCard(
                    game.team2_player2,
                    game.team2_player2_elo_change,
                    game.team2_player2_elo_before,
                    game.team2_player2_elo_after,
                    !team1Won
                  )}
                </div>
              </div>
            </div>
            
            {/* Footer info */}
            <div className="mt-3 text-xs text-muted-foreground flex justify-between items-center border-t border-border/30 pt-2">
              <div className={cn(
                "px-2 py-1 rounded text-xs",
                isWinner ? "bg-green-500/20 text-green-400" : 
                isLoser ? "bg-red-500/20 text-red-400" :
                "bg-muted/30 text-muted-foreground"
              )}>
                {isWinner ? "You won!" : isLoser ? "You lost" : "Game"}
              </div>
              <div>{formatDate(game.created_at)}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Calculate player statistics based on game history
  const calculatePlayerStats = () => {
    if (!profile || !games.length) return { wins: 0, losses: 0, totalGames: 0, winRate: '0%' };
    
    let wins = 0;
    let losses = 0;
    
    games.forEach(game => {
      // Check if user is on team 1
      const onTeam1 = game.team1_player1?.id === profile.id || game.team1_player2?.id === profile.id;
      // Check if user is on team 2
      const onTeam2 = game.team2_player1?.id === profile.id || game.team2_player2?.id === profile.id;
      
      if (onTeam1 && game.team1Won) wins++;
      else if (onTeam2 && !game.team1Won) wins++;
      else if ((onTeam1 && !game.team1Won) || (onTeam2 && game.team1Won)) losses++;
    });
    
    const totalGames = wins + losses;
    const winRate = totalGames > 0 ? Math.round((wins / totalGames) * 100) + '%' : '0%';
    
    return { wins, losses, totalGames, winRate };
  };

  return (
    <div className="container px-4 mx-auto max-w-4xl py-6">
      <Tabs defaultValue="rankings" onValueChange={setActiveTab} value={activeTab}>
        <TabsList className="grid w-full grid-cols-3 mb-8">
          <TabsTrigger value="rankings">Rankings</TabsTrigger>
          <TabsTrigger value="history">Game History</TabsTrigger>
          <TabsTrigger value="profile">My Profile</TabsTrigger>
        </TabsList>
        
        <TabsContent value="rankings" className="space-y-6">
          {players.length > 0 && (
            <div className="space-y-8">
              {renderTopPlayers()}
              
              {/* Rankings Table */}
              {players.length > 4 && (
                <div className="overflow-hidden rounded-lg border bg-card shadow-md">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-16">Rank</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Player</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground w-24">ELO</th>
                      </tr>
                    </thead>
                    <tbody>
                      {players.slice(4).map((player, index) => {
                        const rank = index + 5; // Start from rank 5
                        const isCurrentUser = profile?.id === player.id;
                        
                        return (
                          <tr 
                            key={player.id} 
                            className={cn(
                              "border-b",
                              isCurrentUser ? "bg-muted/50" : ""
                            )}
                          >
                            <td className="px-4 py-3 text-sm text-center">{rank}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center">
                                <Avatar className="h-8 w-8 mr-3">
                                  {player.avatar_url ? (
                                    <AvatarImage src={player.avatar_url} alt={player.username} />
                                  ) : (
                                    <AvatarFallback className={isCurrentUser ? "bg-primary/20 text-primary" : ""}>
                                      {getInitial(player.username)}
                                    </AvatarFallback>
                                  )}
                                </Avatar>
                                <div>
                                  <div className="font-medium">{player.username}</div>
                                  {isCurrentUser && (
                                    <div className="text-xs text-primary">You</div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium">
                              {Math.round(player.elo)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="history" className={cn("space-y-6", isPwa && "pt-2")}>
          <div className={cn(
            "flex justify-between items-center mb-6",
            isPwa && "flex-col items-start gap-3"
          )}>
            <div>
              <h2 className="text-2xl font-bold text-foreground">Game History</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {filteredGames.length} {filteredGames.length === 1 ? 'game' : 'games'} {playerFilter ? `involving ${playerFilter.username}` : 'recorded'}
              </p>
            </div>
            
            <div className={cn(
              "flex items-center space-x-2",
              isPwa && "w-full"
            )}>
              {playerFilter && (
                <div className="flex items-center bg-primary/20 border border-primary/30 text-primary px-3 py-1 rounded-md text-sm">
                  <Avatar className="h-5 w-5 mr-2">
                    {playerFilter.avatar_url ? (
                      <AvatarImage src={playerFilter.avatar_url} alt={playerFilter.username} />
                    ) : (
                      <AvatarFallback className="text-xs">
                        {getInitial(playerFilter.username)}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <span className="mr-2">{playerFilter.username}</span>
                  <button 
                    onClick={clearFilters} 
                    className="text-primary/70 hover:text-primary"
                    aria-label="Clear filter"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
              
              <div className={cn("relative", isPwa && "flex-1")}>
                <div className="flex items-center relative">
                  <div className={cn(
                    `flex items-center rounded-md border ${showPlayerSearch ? 'border-primary' : 'border-input'} bg-card px-3 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary`,
                    isPwa && "w-full"
                  )}>
                    <Search className="h-4 w-4 text-muted-foreground mr-2" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search players..."
                      value={playerSearch}
                      onChange={handlePlayerSearchChange}
                      onFocus={() => setShowPlayerSearch(true)}
                      className={cn(
                        "h-9 bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
                        isPwa ? "w-full" : "w-[180px]"
                      )}
                    />
                    {playerSearch && (
                      <button 
                        onClick={() => {
                          setPlayerSearch('');
                          setSearchResults([]);
                          setShowPlayerSearch(false);
                        }}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
                
                {showPlayerSearch && playerSearch && (
                  <div 
                    ref={searchResultsRef}
                    className="absolute right-0 mt-2 w-full bg-card rounded-lg border shadow-lg z-50 py-1 max-h-64 overflow-y-auto"
                  >
                    {searchResults.length > 0 ? (
                      searchResults.map(player => (
                        <button
                          key={player.id}
                          onClick={() => {
                            handlePlayerFilterClick(player);
                            setPlayerSearch('');
                            setSearchResults([]);
                            setShowPlayerSearch(false);
                          }}
                          className="flex items-center w-full px-3 py-2 text-left hover:bg-primary/10"
                        >
                          <Avatar className="h-6 w-6 mr-2">
                            {player.avatar_url ? (
                              <AvatarImage src={player.avatar_url} alt={player.username} />
                            ) : (
                              <AvatarFallback>
                                {getInitial(player.username)}
                              </AvatarFallback>
                            )}
                          </Avatar>
                          <span className="text-sm">{player.username}</span>
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-muted-foreground">
                        No players found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {filteredGames.length > 0 ? (
            <div className="space-y-6">
              {/* Group games by date */}
              {(() => {
                const gamesByDate = {};
                
                // Group games by date
                filteredGames.forEach(game => {
                  const dateKey = formatDate(game.created_at, 'date-only');
                  if (!gamesByDate[dateKey]) {
                    gamesByDate[dateKey] = [];
                  }
                  gamesByDate[dateKey].push(game);
                });
                
                // Render groups
                return Object.entries(gamesByDate).map(([date, gamesOnDate]) => (
                  <div key={date} className="space-y-3">
                    <div className={cn(
                      "sticky top-0 z-10 backdrop-blur-sm bg-background/90 py-2",
                      isPwa && "top-0"
                    )}>
                      <h3 className="text-sm font-medium text-primary/80 uppercase tracking-wider">{date}</h3>
                      <div className="h-px bg-gradient-to-r from-primary/20 via-border to-transparent mt-1"></div>
                    </div>
                    <div className="space-y-4">
                      {gamesOnDate.map((game, index) => renderGameCard(game, index))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center p-8">
                <div className="rounded-full bg-muted p-4 mb-4">
                  <PenLine className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-lg font-medium mb-2">
                  {playerFilter ? `No games found for ${playerFilter.username}` : 'No games recorded yet'}
                </p>
                <p className="text-muted-foreground text-center mb-6 max-w-md">
                  {playerFilter 
                    ? `Try selecting a different player or clear the filter to see all games.`
                    : `Start tracking your games to build your rating and see your progress.`}
                </p>
                {playerFilter && (
                  <Button variant="outline" onClick={clearFilters} className="border-primary/50 hover:border-primary hover:bg-primary/10">
                    Clear Filter
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="profile">
          {profile ? (
            <div className="space-y-6">
              {editingProfile ? (
                <EditProfile 
                  profile={profile} 
                  supabase={supabase}
                  onCancel={() => setEditingProfile(false)}
                  onSuccess={handleProfileUpdated}
                />
              ) : (
                <Card className="overflow-hidden border-t-4 border-t-primary">
                  <CardHeader className="bg-gradient-to-r from-primary/10 to-transparent">
                    <CardTitle>Player Profile</CardTitle>
                    <CardDescription>Your personal information and statistics</CardDescription>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      <div className="flex flex-col items-center space-y-3">
                        <Avatar className="h-24 w-24 ring-2 ring-primary ring-offset-2 ring-offset-background">
                          {profile.avatar_url ? (
                            <AvatarImage src={profile.avatar_url} alt={profile.username} />
                          ) : (
                            <AvatarFallback className="text-3xl bg-primary/20 text-primary">
                              {getInitial(profile.username)}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <div className="text-2xl font-bold">{profile.username}</div>
                        <div className="flex items-center space-x-1 text-muted-foreground">
                          <span>ELO Rating:</span>
                          <span className="font-medium text-primary">{Math.round(profile.elo || 1000)}</span>
                        </div>
                      </div>
                      
                      <div className="flex-1 space-y-4">
                        {/* Use calculated stats instead of profile values */}
                        {(() => {
                          const { wins, losses, totalGames, winRate } = calculatePlayerStats();
                          return (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="p-4 rounded-lg bg-muted/50 border border-accent/20">
                                <div className="text-sm text-muted-foreground">Wins</div>
                                <div className="text-2xl font-bold text-accent">{wins}</div>
                              </div>
                              <div className="p-4 rounded-lg bg-muted/50 border border-destructive/20">
                                <div className="text-sm text-muted-foreground">Losses</div>
                                <div className="text-2xl font-bold text-destructive">{losses}</div>
                              </div>
                              <div className="p-4 rounded-lg bg-muted/50 border border-secondary/20">
                                <div className="text-sm text-muted-foreground">Total Games</div>
                                <div className="text-2xl font-bold text-secondary">{totalGames}</div>
                              </div>
                              <div className="p-4 rounded-lg bg-muted/50 border border-primary/20">
                                <div className="text-sm text-muted-foreground">Win Rate</div>
                                <div className="text-2xl font-bold text-primary">{winRate}</div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="bg-gradient-to-r from-transparent to-muted/30">
                    <Button onClick={() => setEditingProfile(true)} className="bg-primary hover:bg-primary/90">
                      Edit Profile
                    </Button>
                  </CardFooter>
                </Card>
              )}
            </div>
          ) : (
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-muted-foreground mb-4">Profile not found</p>
                  <Button onClick={() => setEditingProfile(true)} className="bg-primary hover:bg-primary/90">
                    Create Profile
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Dashboard;